import { io, Socket } from 'socket.io-client'
import { apiBaseUrl } from '@/const'
import { getActiveToken } from '@/services/axios'

export type PromotionDomainStatus = 'DOMAIN_DEAD' | 'DOMAIN_ACTIVE'

export interface PromotionDomainCheckedPayload {
  promotionId: number
  domainUrl: string
  description?: string
  timestamp: string
  type: PromotionDomainStatus
}

type PromotionSocketEvent = 'domain:checked'

class PromotionLinksSocketService {
  private socket: Socket | null = null
  private isConnected = false
  private listeners: Map<string, Set<(payload: PromotionDomainCheckedPayload) => void>> = new Map()
  private connectPromise: Promise<void> | null = null

  connect(): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve()
    }

    if (this.connectPromise) {
      return this.connectPromise
    }

    this.connectPromise = new Promise((resolve, reject) => {
      const token = getActiveToken()
      if (!token) {
        this.connectPromise = null
        reject(new Error('Missing admin token'))
        return
      }

      this.disconnect()

      const socketBaseUrl = apiBaseUrl.replace(/\/$/, '')

      this.socket = io(`${socketBaseUrl}/promotion-links`, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        auth: {
          adminToken: token,
        },
        query: {
          source: 'admin',
        },
      })

      const onConnect = () => {
        this.isConnected = true
        this.connectPromise = null
        cleanupListeners()
        resolve()
      }

      const onConnectError = (error: Error) => {
        if (!this.isConnected) {
          this.connectPromise = null
          cleanupListeners()
          reject(error)
        }
      }

      const cleanupListeners = () => {
        this.socket?.off('connect', onConnect)
        this.socket?.off('connect_error', onConnectError)
      }

      this.socket.on('connect', onConnect)
      this.socket.on('connect_error', onConnectError)

      this.socket.on('disconnect', () => {
        this.isConnected = false
      })

      this.socket.on('domain:checked', (payload: PromotionDomainCheckedPayload) => {
        this.emit('domain:checked', payload)
      })
    })

    return this.connectPromise
  }

  disconnect(): void {
    this.connectPromise = null
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
    }
  }

  on(event: PromotionSocketEvent, callback: (payload: PromotionDomainCheckedPayload) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)
  }

  off(event: PromotionSocketEvent, callback: (payload: PromotionDomainCheckedPayload) => void): void {
    this.listeners.get(event)?.delete(callback)
  }

  private emit(event: PromotionSocketEvent, payload: PromotionDomainCheckedPayload): void {
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(payload)
      } catch (error) {
        console.error(`Error in promotion socket listener (${event}):`, error)
      }
    })
  }

  isSocketConnected(): boolean {
    return this.isConnected && !!this.socket?.connected
  }
}

export const promotionLinksSocketService = new PromotionLinksSocketService()
